import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IArticle } from '../../../models/article.model';
import { ICostAccountingType } from '../../../models/cost-accounting-type.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { ICostType } from '../../../models/cost-type.model';
import { ICostUnitGroupOverview } from '../../../models/cost-unit-group-overview.model';
import { ICostUnit } from '../../../models/cost-unit.model';
import { ICostingLevel } from '../../../models/costing-level.model';
import { CostingLevelTypeEnum } from '../../../models/enum/costing-level-type.enum';
import { IMeasureUnit } from '../../../models/measure-unit.model';
import { ICostingLevelNode } from '../../../models/util/kalkulationsstufe-node.model';
import { ArticleResource } from '../../../resources/article-resource.service';
import { CostAccountingTypeResource } from '../../../resources/cost-accounting-type.resource';
import { CostCenterResource } from '../../../resources/cost-center.resource';
import { CostTypeResource } from '../../../resources/cost-type.resource';
import { CostUnitGroupResource } from '../../../resources/cost-unit-group.resource';
import { CostUnitResource } from '../../../resources/cost-unit.resource';
import { CostingLevelResource } from '../../../resources/costing-level.resource';
import { MeasureUnitResource } from '../../../resources/measure-unit.resource';
import { createLookupById } from '../../../utils/lookup-utils';
import { ISharedDataState } from './shared-data-state.model';

let _state: ISharedDataState = {
    costCenterLookup: {},
    costTypeLookup: {},
    costUnitGroups: [],
    costingLevelTree: [],
    costingLevelLookup: {},
    costAccountingTypeLookup: {},
    articleLookup: {},
    isLoading: false,
    costUnitLookup: {},
    measureUnitLookup: {},
};

@Injectable({
    providedIn: 'root',
})
export class SharedDataFacade {

    protected store$: BehaviorSubject<ISharedDataState> = new BehaviorSubject<ISharedDataState>(_state);

    constructor(private myMeasureUnitResource: MeasureUnitResource,
                private myCostUnitResource: CostUnitResource,
                private myCostingLevelResource: CostingLevelResource,
                private myArticleResource: ArticleResource,
                private myCostAccountingTypeResource: CostAccountingTypeResource,
                private myCostUnitGroupResource: CostUnitGroupResource,
                private myCostCenterResource: CostCenterResource,
                private myCostTypeResource: CostTypeResource) {
    }

    public getStateSnapshot(): ISharedDataState {
        return this.store$.value;
    }


    public subState(): Observable<ISharedDataState> {
        return this.store$.asObservable();
    }

    public fetchCostCenters(forceRefresh?: boolean): Observable<ICostCenter[]> {
        const costCentersInState = Object.values(_state.costCenterLookup);

        if (costCentersInState.length > 0 && !forceRefresh) {
            return of(costCentersInState);
        }

        this.setIsloading(true);
        return this.myCostCenterResource.getCostCenters()
            .pipe(tap((costCenters: ICostCenter[]) => {
                const costCenterLookup: Record<number, ICostCenter> = createLookupById(costCenters, 'id');
                this.updateState({ ..._state, costCenterLookup });
            }),
            catchError(() => []),
            finalize(() => this.setIsloading(false)));
    }

    public fetchCostTypes(forceRefresh?: boolean): Observable<ICostType[]> {
        const costTypesInState = Object.values(_state.costTypeLookup);

        if (costTypesInState.length > 0 && !forceRefresh) {
            return of(costTypesInState);
        }

        this.setIsloading(true);
        return this.myCostTypeResource.getCostTypes(false, false, false)
            .pipe(tap((costCenters: ICostType[]) => {
                const costTypeLookup: Record<number, ICostType> = createLookupById(costCenters, 'id');
                this.updateState({ ..._state, costTypeLookup });
            }),
            catchError(() => []),
            finalize(() => this.setIsloading(false)));
    }

    public fetchMeasureUnits(forceRefresh?: boolean): Observable<IMeasureUnit[]> {
        const measureUnitsInState: IMeasureUnit[] = Object.values(_state.measureUnitLookup);
        if (measureUnitsInState.length > 0 && !forceRefresh) {
            return of(measureUnitsInState);
        }
        this.setIsloading(true);
        return this.myMeasureUnitResource.getAllMeasureUnits()
            .pipe(
                tap((measureUnits: IMeasureUnit[]) => {
                    const measureUnitLookup: Record<number, IMeasureUnit> = createLookupById(measureUnits, 'id');
                    this.updateState({ ..._state, measureUnitLookup });
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }

    public fetchCostAccountingTypes(forceRefresh?: boolean): Observable<ICostAccountingType[]> {
        const typesInState: ICostAccountingType[] = Object.values(_state.costAccountingTypeLookup);
        if (typesInState.length > 0 && !forceRefresh) {
            return of(typesInState);
        }
        this.setIsloading(true);
        return this.myCostAccountingTypeResource.getAllCostAccountingTypes()
            .pipe(
                tap((costAccountingTypes: ICostAccountingType[]) => {
                    const costAccountingTypeLookup: Record<number, ICostAccountingType> = createLookupById(costAccountingTypes, 'id');
                    this.updateState({ ..._state, costAccountingTypeLookup });
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }


    public fetchArticles(forceRefresh?: boolean): Observable<IArticle[]> {
        const articlesInState: IArticle[] = Object.values(_state.articleLookup);
        if (articlesInState.length > 0 && !forceRefresh) {
            return of(articlesInState);
        }
        this.setIsloading(true);
        return this.myArticleResource.getArticles()
            .pipe(
                tap((articles: IArticle[]) => {
                    const articleLookup: Record<number, IArticle> = createLookupById(articles, 'id');
                    this.updateState({ ..._state, articleLookup });
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));

    }


    public fetchCostUnits(forceRefresh?: boolean): Observable<ICostUnit[]> {
        const costUnitsInState: ICostUnit[] = Object.values(_state.costUnitLookup);
        if (costUnitsInState.length > 0 && !forceRefresh) {
            return of(costUnitsInState);
        }
        this.setIsloading(true);
        return this.myCostUnitResource.getAllCostUnits()
            .pipe(
                tap((costUnits: ICostUnit[]) => {
                    const costUnitLookup: Record<number, ICostUnit> = createLookupById(costUnits, 'id');
                    this.updateState({ ..._state, costUnitLookup });
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }

    public fetchCostUnitGroups(forceRefresh?: boolean): Observable<ICostUnitGroupOverview[]> {
        const costUnitGroupsInState: ICostUnitGroupOverview[] = Object.values(_state.costUnitGroups);
        if (costUnitGroupsInState.length > 0 && !forceRefresh) {
            return of(costUnitGroupsInState);
        }
        this.setIsloading(true);
        return this.myCostUnitGroupResource.getAllCostUnitGroups()
            .pipe(
                tap((costUnitGroups: ICostUnitGroupOverview[]) => {
                    this.updateState({ ..._state, costUnitGroups });
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)));
    }


    public fetchCostingLevels(forceRefresh?: boolean): Observable<ICostingLevel[]> {
        const costingLevelsInState: ICostingLevel[] = Object.values(_state.costingLevelLookup);

        if (costingLevelsInState.length > 0 && !forceRefresh) {
            return of(costingLevelsInState);
        }
        return this.myCostingLevelResource.getAllCostingLevels()
            .pipe(
                tap((costingLevels: ICostingLevel[]) => {
                    const costingLevelLookup: Record<number, ICostingLevel> = createLookupById(costingLevels, 'id');
                    const costingLevelTree: ICostingLevelNode[] = this.createCostingLevelTree(costingLevels);
                    this.updateState({ ..._state, costingLevelLookup, costingLevelTree });
                }),
                catchError(() => []),
                finalize(() => this.setIsloading(false)),
            );
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    /**
     * Creates a list of all root parent-costingLevels. CostingLevels contain a list of their children
     * @param costingLevels
     * @private
     */
    private createCostingLevelTree(costingLevels: ICostingLevel[]): ICostingLevelNode[] {
        const sorted: ICostingLevelNode[] = costingLevels.sort((a, b) => (a.parentCostingLevelId < b.parentCostingLevelId ? 1 : -1)) as ICostingLevelNode[];

        const map = {};
        let node: ICostingLevelNode;
        const roots: ICostingLevelNode[] = [];
        let i: number;

        for (i = 0; i < sorted.length; i += 1) {
            map[ sorted[ i ].id ] = i; // initialize the map
            sorted[ i ].children = []; // initialize the children
        }

        for (i = 0; i < sorted.length; i += 1) {
            node = sorted[ i ];
            if (node.parentCostingLevelId !== null) {
                if (map[ node.parentCostingLevelId ]) {
                    sorted[ map[ node.parentCostingLevelId ] ].children.push(node);
                    sorted[ map[ node.parentCostingLevelId ] ].children.sort((a, b) => (a.other === b.other ? 0 : a.other ? 1 : -1));
                }
            } else {
                roots.push(node);
            }
        }
        const order: string[] = [
            CostingLevelTypeEnum.DIRECT_MATERIAL_COST.valueOf(),
            CostingLevelTypeEnum.INDIRECT_MATERIAL_COST.valueOf(),
            CostingLevelTypeEnum.DIRECT_PRODUCTION_COST.valueOf(),
            CostingLevelTypeEnum.INDIRECT_PRODUCTION_COST.valueOf(),
            CostingLevelTypeEnum.INDIRECT_ADMINISTRATION_COST.valueOf(),
            CostingLevelTypeEnum.INDIRECT_DISTRIBUTION_COST.valueOf(),
            null,
        ];

        roots.sort((a, b) => order.indexOf(a.costingLevelType) - order.indexOf(b.costingLevelType));
        return roots;
    }

    private updateState(state: ISharedDataState): void {
        this.store$.next(_state = state);
    }
}
